import { Link } from "react-router-dom";
import { useLocale } from "@/context/LocaleContext";
import { imgUrl } from "@/lib/api";

function rupees(amount, formatMoney) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  if (formatMoney) return formatMoney(amount, "INR");
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount));
}

export default function EarnKaroCard({ product }) {
  const locale = useLocale();
  const formatMoney = locale?.formatMoney;
  const image = imgUrl(product?.image || product?.images?.[0]);
  const buy = product?.buy_url && /^https:\/\//i.test(product.buy_url) ? product.buy_url : "";
  const price = product?.price;
  const mrp = product?.mrp_inr;
  const discount = product?.discount_pct;
  const showMrp = mrp != null && price != null && Number(mrp) > Number(price);
  if (!product?.title || price == null || !image || !buy || !product?.id) return null;
  const listing = {
    id: product.id,
    title: product.title,
    brand: product.brand || "",
    price,
    currency: "INR",
    image,
    images: [image],
    buy_url: buy,
    source: "earnkaro",
    seller_name: product.store || "",
    external: true,
    affiliate: true,
    mrp_inr: mrp,
    discount_pct: discount,
  };
  const detail = `/product/${encodeURIComponent(product.id)}`;
  return (
    <article className="group flex h-full flex-col" data-testid={`earnkaro-card-${product.id}`}>
      <Link to={detail} state={{ product: listing }} className="relative block aspect-[3/4] w-full overflow-hidden bg-white">
        <img src={image} alt={product.title} className="absolute inset-0 h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" decoding="async" />
        {product.store ? (
          <span className="absolute top-3 left-3 bg-aura-ink text-white text-[10px] tracking-luxe uppercase px-2.5 py-1" data-testid="earnkaro-card-store">
            {product.store}
          </span>
        ) : null}
        {discount != null && discount !== "" ? (
          <span className="absolute top-3 right-3 bg-aura-accent text-white text-[10px] px-2.5 py-1" data-testid="earnkaro-card-discount">
            {Number(discount)}% off
          </span>
        ) : null}
      </Link>
      <div className="mt-3 flex min-h-[4.75rem] items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={detail} state={{ product: listing }} className="block min-h-[2.5rem] font-serif text-base leading-tight line-clamp-2 hover:text-aura-accent">
            {product.title}
          </Link>
          <p className="mt-0.5 h-4 truncate text-xs text-aura-muted">{product.brand || product.store}</p>
        </div>
        <div className="mt-0.5 shrink-0 text-right">
          <div className="font-mono text-sm" data-testid="earnkaro-card-price">{rupees(price, formatMoney)}</div>
          {showMrp ? (
            <div className="text-xs text-aura-muted line-through" data-testid="earnkaro-card-mrp">{rupees(mrp, formatMoney)}</div>
          ) : (
            <div className="text-xs invisible" aria-hidden="true">—</div>
          )}
        </div>
      </div>
      <a
        href={buy}
        target="_blank"
        rel="sponsored noopener"
        className="mt-3 inline-flex h-10 w-full items-center justify-center text-sm font-medium btn-accent"
        data-testid="earnkaro-card-buy"
      >
        Buy now
      </a>
    </article>
  );
}

export const INDIA_BROWSE = [
  ["electronics", "Electronics"],
  ["grocery-daily-essentials", "Grocery"],
  ["men-fashion", "Men"],
  ["women-fashion", "Women"],
  ["footwear", "Footwear"],
  ["beauty", "Beauty"],
  ["health-personal-care", "Health"],
  ["baby-products", "Baby"],
  ["home-kitchen", "Home"],
];

export function IndiaBrowse({ selected, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="india-browse" role="group" aria-label="India categories">
      <button
        type="button"
        onClick={() => onChange("")}
        data-testid="india-browse-all"
        className={`text-xs px-3 py-1.5 border ${!selected ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/70"}`}
      >
        All India
      </button>
      {INDIA_BROWSE.map(([slug, label]) => (
        <button
          key={slug}
          type="button"
          onClick={() => onChange(slug)}
          data-testid={`india-browse-${slug}`}
          className={`text-xs px-3 py-1.5 border ${selected === slug ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/70"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
